// src/components/page/article/ArticleCard.tsx
'use client';

import React, { useEffect, useRef, useState } from 'react';
import style from './ArticleList.module.scss';
import Image from 'next/image';
import Link from 'next/link';
import Avatar from '@/components/ui/avatar/Avatar';
import OpenArticleAnchor from '@/components/page/article/OpenArticleAnchor';
import { ArticleHeaderFuncBtn } from '@/components/header/article/BtnClient/Btn';
import { ViewStat, ReplyStat } from '@/components/page/article/ArticleStats';
import {
  ReceiveBtn,
  GiveMyBtn,
  GiveBtn,
  WishBtn,
  ExchangeBtn,
  SecretBtn,
  ReunionBtn,
  LotteryBtn,
  CollectBtn,
} from '@/components/ui/articleList/ArticleListCoinBtn';
import { IconArFill } from '@/components/icons';
import type { Article } from '@/model/article';
import ArticleMediaCarousel from '@/components/page/article/ArticleMediaCarousel';

// 工具
import { formatMessageTimeLabel } from '@/libs/utils/time';

const fallbackParagraph = '';

// 簡單把 HTML tag 移掉，避免列表預覽出現 <a>...</a>
function stripHtml(input: string | undefined): string {
  if (!input) return '';
  return input.replace(/<[^>]+>/g, '');
}

/** 依 bizType 決定要顯示哪種互動按鈕 */
function renderBizAction(article: Article, onBeforeEnterPost?: () => void) {
  // message_get_coin 目前沒有上限，就先固定 false
  const msgSoldOut = false;

  // 判斷chip_give是否已發完
  const chipSoldOut = (article.chipGive?.amount ?? 0) <= 0;

  switch (article.bizType) {
    case 'mood':
      return <ReceiveBtn womoId={article.womoId ?? article.author?.id ?? ''} />;

    case 'message_get_coin':
      return (
        <GiveMyBtn
          articleId={article.id}
          coinAvatarSrc={article.author.avatar}
          onBeforeEnterPost={onBeforeEnterPost}
          soldOut={msgSoldOut}
        />
      );

    case 'coin_give':
      return (
        <GiveBtn
          articleId={article.id}
          coinAvatarSrc={article.chipGive?.typePhoto}
          coinType={article.chipGive?.type}
          onBeforeEnterPost={onBeforeEnterPost}
          soldOut={chipSoldOut}
        />
      );

    case 'coin_receive':
      return <WishBtn />;
    case 'coin_exchange':
      return <ExchangeBtn />;
    case 'secret':
      return <SecretBtn />;
    case 'pair':
      return <ReunionBtn />;
    case 'lottery':
      return <LotteryBtn />;
    case 'kol_privilege':
      return <CollectBtn />;
    default:
      return null;
  }
}

type Props = {
  article: Article;
  onBeforeEnterPost?: () => void;
  /** 刪除成功時通知父層（例如 ArticleList 直接把該卡片 filter 掉） */
  onDeleted?: (articleId: string) => void;
};

export default function ArticleCard({ article, onBeforeEnterPost, onDeleted }: Props) {
  const a = article;
  const title = a.title || undefined;
  const createdTime = a.createdAt || '';

  const isStoreArticle = a.bizType === 'store_post';

  // ⭐ 使用 womoId 為主，沒有時 fallback 到 author.id
  const userSlug = a.womoId ?? a.author?.id ?? '';

  // 內容：先移除 html，再以空行分段
  const contentParagraphs = (() => {
    const raw = stripHtml(a.content) || fallbackParagraph;
    return raw.split(/\n{2,}/g);
  })();

  // ⭐ 把 join 結果抽成變數，給 useEffect 當依賴用，避免「複雜表達式」
  const contentKey = contentParagraphs.join('\n');

  const actionEl = renderBizAction(a, onBeforeEnterPost);

  // ⭐ 判斷內容是否超出兩行（用 scrollHeight vs clientHeight）
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const checkOverflow = () => {
      if (!el) return;
      const hasOverflow = el.scrollHeight - el.clientHeight > 1;
      setIsOverflow(hasOverflow);
    };

    checkOverflow();

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        checkOverflow();
      });
      ro.observe(el);
      return () => ro.disconnect();
    } else {
      window.addEventListener('resize', checkOverflow);
      return () => window.removeEventListener('resize', checkOverflow);
    }
  }, [contentKey]);

  // 判斷有沒有任何媒體（圖片 / YouTube / 抖音 / 音訊 / 本機影片）
  const hasAnyMedia =
    (Array.isArray(a.covers) && a.covers.length > 0) ||
    !!a.cover ||
    !!a.url ||
    !!a.tiktokId ||
    !!a.music ||
    !!(a.shortData && a.shortData.length > 0);

  return (
    <div className={`rounded-4 shadow-sm p-5 mb-5 ${style.listOutline}`}>
      <div>
        {/* 作者 / 店家區塊 */}
        <div className="mb-3">
          <div className="d-flex align-items-center mb-2">
            <div className="flex-shrink-0">
              {isStoreArticle ? (
                // 店家文章：連到 store
                <Link href={`/store/${a.storeOpenId ?? ''}`}>
                  <Avatar src={a.storePhoto || a.author?.avatar} size={42} />
                </Link>
              ) : (
                // 其他文章：連到 user（用 womoId 為主）
                <Link href={`/user/${userSlug}`}>
                  <Avatar src={a.author?.avatar} size={42} />
                </Link>
              )}
            </div>

            <div className="ms-2 flex-shrink-1 flex-grow-1 min-w-0">
              <div className="d-flex align-items-center">
                {/* 顯示名稱：店家文章顯示店名，其他顯示作者 */}
                <span className="fw-bold flex-shrink-1 text-truncate-1 min-w-0">
                  {isStoreArticle ? (
                    <Link href={`/store/${a.storeOpenId ?? ''}`}>{a.storeName ?? '店家'}</Link>
                  ) : (
                    <Link href={`/user/${userSlug}`}>{a.author?.name ?? '匿名'}</Link>
                  )}
                </span>

                {(a.emoji?.text || a.emoji?.pic) && (
                  <>
                    <span className="d-flex align-items-center flex-shrink-0 ms-1 fs-sm-1 text-muted">
                      {' — 覺得 '}
                      {a.emoji.text && <span>{a.emoji.text} </span>}
                      {a.emoji.pic && (
                        <Image
                          src={a.emoji.pic}
                          alt={a.emoji.text || 'emoji'}
                          width={20}
                          height={20}
                          className="ms-1 align-text-bottom"
                        />
                      )}
                    </span>
                  </>
                )}
              </div>

              <div className="d-flex align-items-center text-green fs-sm-2">
                <span className="text-grey-700 flex-shrink-0">
                  {formatMessageTimeLabel(createdTime)}
                  {a.ar.isAR && '．'}
                  {/* {createdTime} */}
                </span>
                {a.ar.isAR && (
                  <span
                    className={`${style.arTag} d-inline-flex align-items-center flex-grow-1 flex-shrink-1`}
                  >
                    <Link
                      href={`/ar-space/${a.ar.arSpaceId}/?type=${a.ar.arType}`}
                      className="d-flex align-items-center w-100"
                    >
                      <span className="flex-shrink-0 me-1">
                        <IconArFill width={22} />
                      </span>

                      <span className={style.arTitleText}>
                        {a.ar.ar_title}
                        空間
                      </span>
                    </Link>
                  </span>
                )}
              </div>
            </div>
            <div className="flex-shrink-0 flex-grow-0">
              {/* ⭐ 把 onDeleted 傳給 Header，讓 Header 刪除成功後可以通知 List */}
              <ArticleHeaderFuncBtn article={a} onDeleted={onDeleted} />
            </div>
          </div>

          {/* 標題（如果有） */}
          {title && <h5 className="mb-2">{title}</h5>}

          {/* 內容節錄（line-clamp: 2） */}
          <div ref={contentRef} className={`mt-2 ${style.content}`}>
            {contentParagraphs.map((p, idx) => (
              <p key={`${a.id}-p-${idx}`}>{p}</p>
            ))}
          </div>

          {/* 看更多（只有在超出兩行時才顯示） */}
          {isOverflow && (
            <OpenArticleAnchor
              id={a.id} // 直接用 normalize 後的 id: `${table}_${table_id}`
              beforeNavigate={onBeforeEnterPost}
              aria-label={`開啟 ${
                isStoreArticle ? (a.storeName ?? '店家') : (a.author?.name ?? '作者')
              } 的文章`}
              className="fs-sm-1 text-grey-500"
            >
              看更多
            </OpenArticleAnchor>
          )}

          {/* 多媒體區（圖片 / YouTube / 抖音 / 音訊 / 本機影片） */}
          {hasAnyMedia && (
            <div className="col-12 col-md-12 mt-3">
              <div className="position-relative">
                <OpenArticleAnchor
                  id={a.id}
                  beforeNavigate={onBeforeEnterPost}
                  aria-label={`開啟 ${
                    isStoreArticle ? (a.storeName ?? '店家') : (a.author?.name ?? '作者')
                  } 的文章`}
                >
                  <ArticleMediaCarousel article={a} variant="list" className={style.imgWrapper} />
                </OpenArticleAnchor>
              </div>
            </div>
          )}
        </div>

        {/* 底部統計 + 行為 */}
        <div className="mt-auto">
          <div className="d-flex align-items-center gap-3 text-muted mb-2">
            <ViewStat value={a.stats?.views} suffix="次觀看" />

            <OpenArticleAnchor
              id={a.id}
              beforeNavigate={onBeforeEnterPost}
              aria-label={`開啟 ${
                isStoreArticle ? (a.storeName ?? '店家') : (a.author?.name ?? '作者')
              } 的文章`}
              className="d-inline-flex align-items-center"
            >
              <ReplyStat value={a.stats?.replies ?? 0} suffix="則回覆" />
            </OpenArticleAnchor>
          </div>

          {/* 領取 / 互動區塊（依 bizType 顯示對應按鈕） */}
          {actionEl}
        </div>
      </div>
    </div>
  );
}
